import React, { Component } from "react";
import { connect } from "react-redux";
import { grocerizeMyListTotalSavings, deleteItemFromList } from "../../redux/actions/listActions";
import { showModal } from "../../redux/actions/modalActions";
import { getWordyDate, findColesAndWoolsPricing } from "../../utils/UIFunctions";
import { Icon } from "../icons/Icon";
import ListCardMobile from "./ListCardMobile";
import ListOptionsModal from "./listOptions/ListOptionsModal";
import UnlockPremiumModal from "../popups/UnlockPremiumModal";
import DeleteListItemModalContent from "./listOptions/DeleteListItemModalContent";

interface Props {
  listMetaData: List;
  user: UserState;
  lists: List[];
  grocerizeMyListTotalSavings: any;
  showModal: any;
  deleteItemFromList: (list_id: number, item_id: string | number) => Promise<any>;
}

interface State {
  expandedView: boolean;
}

class ListCard extends Component<Props, State> {
  state = {
    expandedView: false,
  };

  componentDidMount() {
    // Listen for the custom event from the options menu
    document.addEventListener('viewListItems', this.handleViewListItems);
  }
  
  handleDeleteItem = (list_id: number, item_id: string | number, item_name: string, e: React.MouseEvent, list_name: string) => {
    // Get the position of the delete button that was clicked
    const buttonRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const position = {
      top: buttonRect.top + window.scrollY,
      left: buttonRect.left + window.scrollX + buttonRect.width + 5 // Position slightly to the right of the X button
    };
    
    const modalContent = () => (
      <DeleteListItemModalContent 
        list_id={list_id}
        item_id={item_id}
        item_name={item_name}
        list_name={list_name}
        position={position}
      />
    );
    
    this.props.showModal({ component: modalContent });
  }

  componentWillUnmount() {
    // Clean up the event listener
    document.removeEventListener('viewListItems', this.handleViewListItems);
  }

  // Handle the custom event from options menu
  handleViewListItems = (event: CustomEvent) => {
    if (event.detail && event.detail.listId === this.props.listMetaData.id) {
      this.toggleExpandView();
    }
  };

  toggleExpandView = () => {
    this.setState(prevState => ({
      expandedView: !prevState.expandedView
    }));
  };

  clickedOptions = (list: List | undefined) => {
    if (list?.id === undefined) {
      return;
    }

    const button = document.getElementById(list.id.toString());

    if (button) {
      const postionOfButton = button.getBoundingClientRect();

      const modalContent = this.props.user?.isPremium
          ? () => (
              <div
                  style={{
                    marginTop: postionOfButton.bottom + 20 + "px", // fudge factor as the button size is not working?
                    marginLeft: postionOfButton.left - 40 + "px", // place content left and caret right
                    width: postionOfButton.right - postionOfButton.left + "px",
                  }}
              >
                {/* Use JSX spread syntax with type assertion to handle the props properly */}
                <ListOptionsModal {...({ list } as any)} />
              </div>
          )
          : () => <UnlockPremiumModal />;

      this.props.showModal({ component: modalContent });
    }
  };

  renderListItems = (list) => {
    const items = list.items || [];
    
    if (items.length === 0) {
      return (
        <div className="u-text-center u-p--24 u-background--grey-light">
          <div className="u-opacity--7 u-main-font--small">
            No items in this list
          </div>
        </div>
      );
    }
    
    return (
      <div className="u-w-all" style={{ overflowX: 'hidden' }}>
        <div className="u-background--white">
          {items.map((item, index) => {
            const { colesPricing, woolsPricing } = findColesAndWoolsPricing(item);
            
            return (
              <div key={index} className="u-border--bottom--grey u-pv--8">
                {/* Use a flex layout that matches the header columns */}
                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  {/* Item image and details - 25% width like the title above */}
                  <div className="u-w-25pct u-flex u-align-center">
                    <div style={{ width: '40px', marginRight: '8px' }}>
                      {item.image_url ? (
                        <img 
                          src={item.image_url} 
                          alt={item.name} 
                          className="u-w-all u-h-all u-object-fit--contain"
                        />
                      ) : (
                        <div className="u-w-all u-h-all u-background--grey-light u-flex u-justify-center u-align-center">
                          <Icon id="grocerize" size="s" stroke={"#aaa"} />
                        </div>
                      )}
                    </div>
                    
                    <div className="u-pr--8">
                      <div className="u-main-font--small u-semi-bold u-line-clamp--2">
                        {item.name}
                      </div>
                      <div className="u-main-font--vsmall u-color-charcoal u-mt--4">
                        Qty: {item.quantity || 1}
                      </div>
                    </div>
                  </div>
                  
                  {/* Date section - Match width with date above */}
                  <div style={{ width: '116px' }} className="u-text-right u-ph--8">
                    {/* Empty to align with date above */}
                  </div>
                  
                  {/* Coles Price - Match the date column width */}
                  <div style={{width: "116px"}} className="u-flex u-align-center u-ph--8">
                    <div style={{ 
                      backgroundColor: '#f9f9f9', 
                      padding: '8px 12px', 
                      borderRadius: '4px', 
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      width: '100%',
                      border: '1px solid #eee'
                    }}>
                      <div className="u-flex">
                        <span className="u-color-coles u-bold">C</span>
                        <span className="u-ml--8 u-bold">
                          {colesPricing && colesPricing.available 
                            ? `$${colesPricing.price?.toFixed(2) || "-"}` 
                            : <span className="u-color-grey">N/A</span>}
                        </span>
                      </div>
                      {colesPricing?.cup_price && (
                        <div className="u-main-font--vsmall u-color-charcoal u-mt--2">
                          ${colesPricing.cup_price.toFixed(2)}/{colesPricing.cup_volume || '100g'}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Woolworths Price - Match the price column width */}
                  <div style={{width: "116px"}} className="u-flex u-align-center u-ph--8">
                    <div style={{ 
                      backgroundColor: '#f9f9f9', 
                      padding: '8px 12px', 
                      borderRadius: '4px', 
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      width: '100%',
                      border: '1px solid #eee'
                    }}>
                      <div className="u-flex">
                        <span className="u-color-woolworths u-bold">W</span>
                        <span className="u-ml--8 u-bold">
                          {woolsPricing && woolsPricing.available 
                            ? `$${woolsPricing.price?.toFixed(2) || "-"}` 
                            : <span className="u-color-grey">N/A</span>}
                        </span>
                      </div>
                      {woolsPricing?.cup_price && (
                        <div className="u-main-font--vsmall u-color-charcoal u-mt--2">
                          ${woolsPricing.cup_price.toFixed(2)}/{woolsPricing.cup_volume || '100g'}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Delete button aligned with items count above */}
                  <div style={{ width: '90px' }} className="u-mh--8 u-text-right">
                    <button 
                      className="u-button--icon u-color-red-hover"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.handleDeleteItem(list.id, item.id || item.shopify_id, item.name, e, list.title);
                      }}
                      aria-label="Remove item"
                    >
                      <span style={{ fontWeight: 'bold' }}>✕</span>
                    </button>
                  </div>
                  
                  {/* Options button spacing */}
                  <div style={{ width: '40px' }}></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  render() {
    const list = this.props.listMetaData;
    const isUserPremium = this.props.user?.isPremium;

    if (!list) {
      return null;
    }
    return (
        <>
          <div
              className={`u-hide--desktop ${!isUserPremium ? "u-opacity--5" : ""}`}
          >
            <ListCardMobile listMetaData={list}/>
          </div>
          <div
              className={`u-hide--mobile u-ph--48 u-pv--12 u-border--bottom--grey u-flex u-justify-center ${
                  !isUserPremium ? "u-opacity--5" : ""
              }`}
              style={{height: "121px"}}
          >
            <div
                className="u-w-80pct u-flex u-align-center u-main-font--vsmall"
                style={{fontSize: "14px"}}
                id={`list-card-content-${list.id}`}
                data-list-id={list.id}
            >
              <div className="u-w-25pct u-flex u-align-center">
                <div>{list.title}</div>
              </div>

              <div
                  style={{width: "116px"}}
                  className=" u-text-oneline u-text-right u-ph--8 u-flex u-align-center u-justify-end"
              >
                {getWordyDate(list.createdAt)}
              </div>
              <div
                  className=" u-bold u-background--grey u-mh--8 u-h-all u-flex u-align-center u-justify-end"
                  style={{width: "116px"}}
              >
                <div className="u-ph--24 u-flex u-align-center u-flex">
                  ${list?.grocerizeShopTotal.toFixed(2)}
                </div>
              </div>
              <div
                  style={{width: "116px"}}
                  className="u-text-right  u-background--magenta u-mh--8 u-h-all u-flex u-align-center u-justify-end"
              >
                <div className="u-ph--24 u-flex u-align-center u-flex u-bold u-color-white">
                  ${grocerizeMyListTotalSavings(list.items)}
                </div>
              </div>
              <div
                  style={{width: "90px"}}
                  className=" u-bold u-background--grey u-mh--8 u-h-all u-flex u-align-center u-justify-end"
              >
                <div className="u-ph--24 u-flex u-align-center u-flex">
                  {list.itemsCount}
                </div>
              </div>
              <div className="u-w-auto u-flex u-flex-column">
                <div className="u-mb--4">
                  <button
                    id={list.id?.toString()}
                    className="u-btn--pill u-pointer u-color-magenta u-pr--8"
                    style={{ fontSize: '100%' }}
                    onClick={() => this.clickedOptions(list)}
                  >
                    Options <Icon id="chevron_right" size="xs" />
                  </button>
                </div>
                <div>
                  <button
                    className="u-btn--pill u-pointer u-pr--8"
                    style={{ fontSize: '100%' }}
                    onClick={() => this.toggleExpandView()}
                  >
                    {this.state.expandedView ? 'Hide List' : 'View List'} 
                    <Icon id="chevron_right" size="xs" stroke="#9c2bad" fill="none" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          {this.state.expandedView && (
            <div className="u-hide--mobile u-border--bottom--grey u-ph--48 u-flex u-justify-center">
              <div className="u-w-80pct" style={{ overflow: 'hidden' }}>
                {this.renderListItems(list)}
              </div>
            </div>
          )}
        </>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  user: state.user,
  lists: state.lists.lists,
});

export default connect(mapStateToProps, {
  showModal,
  grocerizeMyListTotalSavings,
  deleteItemFromList,
})(ListCard);
